import { nearestAerodrome } from '@/lib/aerodromes';
import type { AerodromeRef, TrackPoints } from '@/lib/db';
import { haversineNm } from '@/lib/parse';
import { isNight } from '@/lib/solar';

/**
 * Counting a track's takeoffs, landings and touch-and-goes.
 *
 * The naive reading — "altitude dipped near the track's lowest fix, so that
 * was a landing" — over-counts badly on real recordings, and the two ways it
 * does so are the two things this module exists to prevent:
 *
 *  - **A break in the track.** ADS-B coverage drops out mid-flight all the
 *    time: a receiver loses the aircraft over a range of hills and picks it
 *    up again twenty minutes later. Altitude-only detection sees the fix
 *    before the gap and the fix after it as a continuous descent to the
 *    ground and a climb away from it, and books a landing and a takeoff for
 *    an aeroplane that never stopped flying. A circuit-shaped track with
 *    half a dozen dropouts came out as half a dozen touch-and-goes.
 *  - **A low pass or a valley.** The lowest fix of a track is only "the
 *    ground" if the aircraft was actually on it. Altitude alone cannot tell
 *    a landing from a run down a riverbed.
 *
 * So the detector never reads altitude on its own. A fix is on the ground
 * when it is both slow and low — measured against the surface *near it in
 * time*, not the track's own floor — with groundspeed coming from the source
 * where it has it and from the fixes themselves where it doesn't; a stretch
 * of ground fixes is only a real ground contact if it lasted long enough, or
 * happened at a known aerodrome (`lib/aerodromes`), or is where the
 * recording itself started or stopped; and a transition that straddles a gap
 * in the recording is not counted at all unless the ground side of it sits
 * at an aerodrome, because a gap is exactly the case where the app cannot
 * see what happened.
 *
 * ## What "slow" has to mean
 *
 * An early version drew the line at taxi speed and missed most of the events
 * in a real FR24 export. A light single crosses the threshold at 60–70kt
 * groundspeed and is away again at much the same speed, so a touch-and-go
 * sampled every six seconds never produces a fix under 40kt: the rollout
 * lands in the band between taxiing and flying and was thrown away as
 * unreadable. The same band swallowed whole departures — a track that begins
 * on the takeoff roll opens at 0ft and 59kt, which is unambiguously on a
 * runway and was being read as neither ground nor air.
 *
 * So there are two heights, not one. Within `SURFACE_MARGIN_FT` of the local
 * ground reference the aircraft is *on the surface* and anything below
 * flying speed is a ground contact; between there and `NEAR_GROUND_MARGIN_FT`
 * only taxi speed counts. A low pass stays safe because height is measured
 * against a nearby surface, not the track minimum — 500ft down a valley
 * floor a thousand feet above the departure aerodrome is 1500ft on the
 * altimeter — and because a ground contact away from any listed aerodrome
 * still has to slow to taxi speed to be believed.
 *
 * ## Where the recording starts and stops
 *
 * A track that ends 150ft over the threshold at 69kt ended because the
 * receiver stopped hearing the aircraft on short final, and the landing that
 * followed is not in the file. Treating the last fixes as an ordinary ground
 * run — one too brief to survive `MIN_GROUND_MS` — dropped that landing, and
 * dropped the takeoff of every track that opens mid-roll. A ground contact at
 * either edge of the recording is therefore not asked to last: there is no
 * more evidence coming, and what there is says the wheels were down.
 *
 * Coverage can also begin or end with nothing on the ground at all — a
 * recording that stops on short final, or starts already climbing away, with
 * no takeoff or landing in the fixes to find. `edgeEventAerodrome` is the one
 * rule here that counts an event the recording does not hold, and it is kept
 * narrow: only at the two ends, only where nothing else found a ground
 * contact, and only with a listed aerodrome almost directly beside the edge
 * fix (`EDGE_EVENT_AERODROME_RADIUS_NM` — tighter than the radius the rest of
 * the module matches ground contacts against), the surface within reach, and
 * the distance to that aerodrome opening or closing — by a wide margin, not
 * a whisker — to say which way the flight was going. A departure or arrival
 * caught further out than that is left alone; that is a gap in the
 * recording, not a fix this rule can read anything off. Because it is still
 * a guess, a count that came from it lowers `DerivedStats.confidence` to
 * `'low'`, the same flag a broken recording earns, so the grid asks the
 * pilot to confirm it rather than showing it as settled.
 *
 * A track that *opens on the surface* beside an aerodrome is different: FR24
 * sends 0ft while the transponder reports on-ground, and a first fix at 0ft
 * and 87kt a quarter of a mile from Ashburton is the takeoff roll, whatever
 * the groundspeed says. There the distance trend is not asked for — a circuit
 * session never opens it by two miles — because the fix itself already says
 * the wheels were on the runway.
 *
 * ## The runway dropout
 *
 * Nearer the ground than a receiver can see, a touch-and-go is not in the
 * recording at all. A real FR24 export of four circuits at Ashburton reads
 * the same way every time: final approach down to 200ft and 60kt over the
 * threshold, forty seconds of silence, then the aircraft again at the far end
 * of the runway at 225ft and 85kt, climbing. No fix is slow, none is on the
 * surface, and the detector counted one landing for five. `runwayGapAt`
 * reads that silence as a ground contact, and only when all of it holds:
 * a short gap, both fixes either side of it low and almost over the same
 * listed aerodrome, both flying the line between them — the runway — and the
 * ground covered across the gap well short of what the speeds either side
 * of it account for. That last test is what separates a rollout from a low
 * approach that merely went out of coverage: an aircraft that stayed airborne
 * averages the speeds either side, one that landed slowed down in between.
 * Like the edge rule it is an inference, so it marks the result
 * (`GroundEvents.gapInferred`) for the pilot to confirm.
 *
 * ## What it still cannot do
 *
 * The surface is found from the track's own slow fixes, so a track that both
 * departs from high ground *and* opens mid-roll — no taxi-out anywhere in the
 * file to read the strip's elevation from — has nothing nearby to measure
 * that departure against, and falls back to the nearest slow fix in time,
 * which may be the arrival hours later and a thousand feet lower. The
 * departure is then missed. Bundled aerodrome elevations would fix it;
 * `lib/aerodromes` does not carry them yet.
 *
 * Near a field the track was on the ground at, heights are measured against
 * the lowest altitude the track reported there (`fieldFloorsFt`), because
 * FR24's on-ground 0ft and its airborne pressure altitudes are not in the
 * same datum. A field the aircraft only touched-and-went at, with no taxi
 * and no on-ground report anywhere in the file, has no floor of its own and
 * falls back to the reference above — at Taupo, over 1300ft up, its
 * touch-and-goes are then missed, for the same reason and with the same fix.
 *
 * Beyond that rule's short, straight, slowed-down silence over a runway,
 * nothing is inferred at a *mid-track* dropout, however suggestive: a gap is
 * the one place where the aircraft could have done anything, and the edge
 * rules deliberately do not apply there. Under-counting stays the direction
 * of the error wherever the evidence runs out in the middle. Every number here lands in a draft the pilot reviews —
 * `PLAN.md` §2 — and a missing landing they add back is a better failure than
 * six invented ones they have to find and strip out.
 */

/** At or below this groundspeed the aircraft is taxiing or stopped, not flying. */
const TAXI_SPEED_KT = 40;

/** At or above this groundspeed it is flying, whatever the altitude says. */
const FLYING_SPEED_KT = 80;

/** How close to the local ground reference still counts as on the ground at taxi speed. */
const NEAR_GROUND_MARGIN_FT = 300;

/**
 * Within this much of the local ground reference the aircraft is on the
 * surface itself, and any speed below `FLYING_SPEED_KT` is a ground contact —
 * the touchdown, the rollout and the first half of the takeoff roll all sit
 * here at 50–70kt. Tight enough that a circuit at 1000ft, or an approach
 * still half a mile out, is nowhere near it.
 */
const SURFACE_MARGIN_FT = 150;

/**
 * Ground elevation is read from slow fixes within this much of the fix being
 * classified, so a flight between aerodromes 1500ft apart in elevation is
 * measured against the right one at each end.
 */
const GROUND_REF_WINDOW_MS = 30 * 60_000;

/** A gap longer than this — and longer than the track's own sampling — is a break in the recording. */
const MIN_BREAK_MS = 3 * 60_000;

/** A gap this many times the track's typical sample interval is a break too. */
const BREAK_SAMPLE_FACTOR = 6;

/**
 * However sparse the recording, a gap this long is a break. Six times the
 * sample interval is the right shape of rule for a one-second GPX; applied
 * without a ceiling to a trace that samples every five minutes it would
 * excuse half an hour of silence, which is long enough to land, refuel and
 * depart again.
 */
const MAX_BREAK_MS = 15 * 60_000;

/** A climb has to hold for this long before it counts as a departure rather than noise. */
const MIN_AIRBORNE_MS = 60_000;

/** A ground contact away from any known aerodrome has to last this long to count. */
const MIN_GROUND_MS = 20_000;

/** A ground contact shorter than this, with a departure straight after it, is a touch-and-go. */
const TOUCH_AND_GO_MAX_GROUND_MS = 90_000;

/**
 * How close the edge fix itself has to be to a listed aerodrome before
 * `edgeEventAerodrome` will read it as a departure from, or an arrival at,
 * that field — tighter than `AERODROME_MATCH_RADIUS_NM`, which the rest of
 * the module and the from/to autofill use for a fix already known to be a
 * ground contact. This one is asked to explain a fix that is *not* a ground
 * contact, so it asks for the aircraft to have been almost over the runway,
 * not just in the neighbourhood: a track that starts or ends a few miles out
 * is left alone, and whatever happened in those miles is for the pilot to
 * fill in.
 */
const EDGE_EVENT_AERODROME_RADIUS_NM = 1;

/**
 * How high above the local surface a track may begin or end and still be read
 * as a departure from, or an arrival at, the aerodrome beside it. A light
 * aircraft close enough in to clear `EDGE_EVENT_AERODROME_RADIUS_NM` and
 * still climbing away is well under a thousand feet; this stays low enough
 * to exclude one already at circuit height or above, which is a transit, not
 * a departure caught late.
 */
const EDGE_EVENT_HEIGHT_FT = 1200;

/** How long either side of a track's edge the distance trend is read over. */
const EDGE_TREND_MS = 5 * 60_000;

/**
 * How much the distance to the aerodrome has to change across that stretch
 * before the track counts as leaving it or joining it, rather than passing by
 * or manoeuvring near it. This rule is the one inference in the module that
 * is not read straight off a fix, so it asks for a trend an aircraft
 * circling or turning nearby would not produce by accident, not merely one
 * bigger than GPS noise.
 */
const EDGE_TREND_NM = 2;

/**
 * How close to a listed aerodrome a fix has to be for `fieldFloorsFt` to
 * measure it against that field's floor: the runway and the last and first
 * few hundred feet of the approach and climb, not the circuit.
 */
const FIELD_FLOOR_RADIUS_NM = 1;

/**
 * The shortest silence `runwayGapAt` reads as a hidden ground contact, and the
 * multiple of the track's own sampling it has to exceed as well. A fix or two
 * dropped in the circuit is ordinary; a rollout and a takeoff roll take tens
 * of seconds.
 */
const RUNWAY_GAP_MIN_MS = 15_000;
const RUNWAY_GAP_SAMPLE_FACTOR = 4;

/**
 * How close both fixes either side of a runway gap have to be to the same
 * listed aerodrome — the same "almost over the runway" test the edge rule uses.
 */
const RUNWAY_GAP_AERODROME_RADIUS_NM = EDGE_EVENT_AERODROME_RADIUS_NM;

/**
 * How far either fix's track may be from the line between them. A touch-and-go
 * goes in and comes out along the runway; a turn inside the gap does not, and
 * a turn is also what shortens the straight-line distance enough to fake the
 * slowdown test below.
 */
const RUNWAY_GAP_ALIGNMENT_DEG = 30;

/**
 * The ground covered across the gap, as a fraction of what the mean of the
 * speeds either side would cover. Airborne and straight it is close to 1; the
 * four Ashburton touch-and-goes this was written from sit at 0.73–0.77.
 */
const RUNWAY_GAP_SLOWDOWN_RATIO = 0.85;

/**
 * Where one counted takeoff or landing sits in the track, for drawing it —
 * the profile chart marks each one so a count that looks wrong can be traced
 * to the fixes that produced it.
 */
export interface GroundEvent {
  kind: 'takeoff' | 'landing';
  /**
   * The fix the detector changed state at: for a landing, the first fix of
   * the ground contact, often on short final; for a takeoff, the first fix
   * it reads as flying, a little into the climb. The touchdown and liftoff
   * themselves sit between a landing and the takeoff that follows it.
   */
  index: number;
  /** Read off a runway gap or the edge of the recording rather than off fixes on the ground. */
  inferred: boolean;
}

export interface GroundEvents {
  /** Every takeoff and landing counted below, in track order. */
  events: GroundEvent[];
  takeoffCount: number;
  nightTakeoffCount: number;
  /** Touch-and-goes included, as `DerivedStats.landingCount` documents. */
  landingCount: number;
  nightLandingCount: number;
  touchAndGoCount: number;
  /**
   * True when a state change was thrown away for straddling a break in the
   * recording — the counts above are then a floor, not a total, and the
   * caller drops its confidence accordingly.
   */
  breaksSkipped: boolean;
  /**
   * True when a takeoff or landing came from `edgeEventAerodrome` rather than
   * from a ground contact the recording actually shows — the one inference
   * in this module the fixes themselves do not settle. The caller drops its
   * confidence accordingly, same as `breaksSkipped`, so the pilot sees the
   * count as a guess rather than a fact.
   */
  edgeInferred: boolean;
  /**
   * True when a landing or takeoff was read off a gap in coverage over a
   * runway (`runwayGapAt`) rather than off fixes on the ground. Lowers the
   * caller's confidence the same way `edgeInferred` does.
   */
  gapInferred: boolean;
}

type Phase = 'ground' | 'air' | 'unknown';

interface Run {
  phase: 'ground' | 'air';
  startIndex: number;
  endIndex: number;
  durationMs: number;
  /** Whether the recording has a hole between the previous run and this one. */
  brokenFromPrevious: boolean;
  /** Whether any fix in this run is within `AERODROME_MATCH_RADIUS_NM` of a listed aerodrome. */
  atAerodrome: boolean;
  /** Whether any fix in this run sits within `SURFACE_MARGIN_FT` of the ground reference. */
  onSurface: boolean;
  /** Whether any fix in this run is at or below `TAXI_SPEED_KT`. */
  slowedToTaxi: boolean;
  /** Whether this run holds the first or the last classifiable fix of the track. */
  atTrackEdge: boolean;
  /** Whether this ground run is, or absorbed, a contact read off a runway gap. */
  hidden: boolean;
  fixCount: number;
}

function finiteAt(values: Float32Array | Float64Array, index: number): number | undefined {
  const value = values[index];
  return value !== undefined && Number.isFinite(value) ? value : undefined;
}

/**
 * Groundspeed per fix: the source's own where it has it, otherwise the
 * distance and time between this fix and the last usable one.
 *
 * Without this, a GPX from a handheld — no speed channel at all — would fall
 * back to altitude-only detection, which is the failure mode this module is
 * here to fix. A derived speed is coarse, but "under 40 knots" versus "over
 * 80" is not a fine distinction.
 */
function resolveSpeedsKt(points: TrackPoints): (number | undefined)[] {
  const { gsKt, t, lat, lon } = points;
  const speeds: (number | undefined)[] = [];
  let previous: { t: number; lat: number; lon: number } | undefined;
  for (let i = 0; i < lat.length; i += 1) {
    const supplied = finiteAt(gsKt, i);
    const ti = finiteAt(t, i);
    const lati = finiteAt(lat, i);
    const loni = finiteAt(lon, i);
    let derived: number | undefined;
    if (previous !== undefined && ti !== undefined && lati !== undefined && loni !== undefined) {
      const hours = (ti - previous.t) / 3_600_000;
      // A gap wide enough to be a break says nothing about the speed at
      // either end of it, and a zero or backwards step says nothing at all.
      if (hours > 0 && hours * 3_600_000 <= MIN_BREAK_MS) {
        derived = haversineNm(previous.lat, previous.lon, lati, loni) / hours;
      }
    }
    speeds.push(supplied ?? derived);
    if (ti !== undefined && lati !== undefined && loni !== undefined) {
      previous = { t: ti, lat: lati, lon: loni };
    }
  }
  return speeds;
}

/**
 * The ground elevation to measure each fix against: the lowest slow fix
 * within half an hour of it, and where there is none, the nearest slow fix in
 * time rather than the track's own floor.
 *
 * A single track-wide minimum is wrong as soon as the two ends of the flight
 * sit at different elevations — land at a 1300ft strip after departing from
 * sea level and the arrival never comes within 150ft of the track minimum,
 * so the landing is missed entirely. The nearest-in-time fallback matters for
 * the same reason at the *start* of a track that departs from the high strip:
 * there are no slow fixes for the next three hours, and reaching for the
 * global minimum would measure the takeoff roll against the sea-level arrival
 * and call it 1300ft in the air.
 *
 * Almost over an aerodrome the track has been on the ground at, that is
 * overridden by the field's own floor — see `fieldFloorsFt`.
 */
function groundReferenceFt(
  points: TrackPoints,
  speedsKt: (number | undefined)[],
): (index: number) => number | undefined {
  const { altFt, t } = points;
  const slow: { t: number; altFt: number }[] = [];
  let lowest: number | undefined;
  for (let i = 0; i < altFt.length; i += 1) {
    const alt = finiteAt(altFt, i);
    if (alt === undefined) {
      continue;
    }
    lowest = lowest === undefined ? alt : Math.min(lowest, alt);
    const speed = speedsKt[i];
    const ti = finiteAt(t, i);
    if (speed !== undefined && speed <= TAXI_SPEED_KT && ti !== undefined) {
      slow.push({ t: ti, altFt: alt });
    }
  }
  const fieldFloorAt = fieldFloorsFt(points, speedsKt);

  return (index: number) => {
    const floor = fieldFloorAt(index);
    if (floor !== undefined) {
      return floor;
    }
    const ti = finiteAt(t, index);
    if (ti === undefined || slow.length === 0) {
      return lowest;
    }
    let local: number | undefined;
    let nearest: { gapMs: number; altFt: number } | undefined;
    for (const fix of slow) {
      const gapMs = Math.abs(fix.t - ti);
      if (gapMs <= GROUND_REF_WINDOW_MS) {
        local = local === undefined ? fix.altFt : Math.min(local, fix.altFt);
      }
      if (nearest === undefined || gapMs < nearest.gapMs) {
        nearest = { gapMs, altFt: fix.altFt };
      }
    }
    return local ?? nearest?.altFt ?? lowest;
  };
}

/**
 * Per aerodrome the track was on the ground at, the lowest altitude it
 * *reported* within `FIELD_FLOOR_RADIUS_NM` of it — the runway, in the
 * datum the airborne fixes are in — as a lookup by fix.
 *
 * FR24 sends 0ft for a fix the transponder flags as on the ground, and a
 * pressure altitude for every other one, so its taxi fixes and its approach
 * fixes are not in the same datum at all. Measured against those zeros, the
 * runway at Ashburton read 175–225ft on one morning and 325–400ft eleven
 * days later on a lower QNH: the second day's touch-and-goes sat above
 * `NEAR_GROUND_MARGIN_FT` at every fix and were not found at all. The
 * lowest non-zero reading near the field is the aircraft at the fence or on
 * the runway, in whatever datum the source used that day, so a fix near the
 * field is measured against that instead.
 *
 * Only for a field with ground evidence of its own — an FR24 0ft or a fix
 * at taxi speed — because at a field the aircraft only flew over, the lowest
 * reading is the overflight, and measuring against it would put the
 * overflight on the runway.
 */
function fieldFloorsFt(
  points: TrackPoints,
  speedsKt: (number | undefined)[],
): (index: number) => number | undefined {
  const { altFt, lat, lon } = points;
  const fieldAt: (AerodromeRef | undefined)[] = [];
  const grounded = new Set<AerodromeRef>();
  const floors = new Map<AerodromeRef, number>();
  for (let i = 0; i < altFt.length; i += 1) {
    const lati = finiteAt(lat, i);
    const loni = finiteAt(lon, i);
    const match =
      lati === undefined || loni === undefined ? undefined : nearestAerodrome(lati, loni);
    const field =
      match !== undefined && match.distanceNm <= FIELD_FLOOR_RADIUS_NM
        ? match.aerodrome
        : undefined;
    fieldAt.push(field);
    const alt = finiteAt(altFt, i);
    if (field === undefined || alt === undefined) {
      continue;
    }
    const speed = speedsKt[i];
    if (alt === 0 || (speed !== undefined && speed <= TAXI_SPEED_KT)) {
      grounded.add(field);
    }
    if (alt !== 0) {
      const floor = floors.get(field);
      floors.set(field, floor === undefined ? alt : Math.min(floor, alt));
    }
  }
  return (index: number) => {
    const field = fieldAt[index];
    return field !== undefined && grounded.has(field) ? floors.get(field) : undefined;
  };
}

/** The track's typical interval between fixes, or `undefined` with fewer than two timed fixes. */
function medianIntervalMs(t: Float64Array): number | undefined {
  const gaps: number[] = [];
  for (let i = 1; i < t.length; i += 1) {
    const previous = finiteAt(t, i - 1);
    const current = finiteAt(t, i);
    if (previous !== undefined && current !== undefined && current > previous) {
      gaps.push(current - previous);
    }
  }
  if (gaps.length === 0) {
    return undefined;
  }
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)];
}

/** The gap beyond which this track is considered to have stopped recording. */
function breakThresholdMs(t: Float64Array): number {
  const median = medianIntervalMs(t);
  if (median === undefined) {
    return MIN_BREAK_MS;
  }
  // A trace that normally samples every four minutes is not "broken" every
  // four minutes; a one-second GPX genuinely is, after three minutes of
  // silence. Whichever is larger, up to the ceiling.
  return Math.max(MIN_BREAK_MS, Math.min(median * BREAK_SAMPLE_FACTOR, MAX_BREAK_MS));
}

/** Initial great-circle bearing from one fix to another, in degrees true. */
function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = Math.PI / 180;
  const phi1 = lat1 * toRad;
  const phi2 = lat2 * toRad;
  const dLon = (lon2 - lon1) * toRad;
  const y = Math.sin(dLon) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLon);
  return (((Math.atan2(y, x) / toRad) % 360) + 360) % 360;
}

function angleBetweenDeg(a: number, b: number): number {
  const difference = Math.abs(a - b) % 360;
  return difference > 180 ? 360 - difference : difference;
}

/**
 * The direction the aircraft was moving at fix `index`: the source's own
 * track where it has one, otherwise the bearing from the neighbouring fix on
 * the far side of `index` from the gap (`step` says which side that is).
 */
function trackAt(points: TrackPoints, index: number, step: -1 | 1): number | undefined {
  const supplied = finiteAt(points.trackDeg, index);
  if (supplied !== undefined) {
    return supplied;
  }
  const other = index + step;
  const lat = finiteAt(points.lat, index);
  const lon = finiteAt(points.lon, index);
  const otherLat = finiteAt(points.lat, other);
  const otherLon = finiteAt(points.lon, other);
  if (lat === undefined || lon === undefined || otherLat === undefined || otherLon === undefined) {
    return undefined;
  }
  return step === -1
    ? bearingDeg(otherLat, otherLon, lat, lon)
    : bearingDeg(lat, lon, otherLat, otherLon);
}

/**
 * Groundspeed at fix `index`, read the same way as `trackAt`. Not
 * `resolveSpeedsKt`: its derived speed for the fix after a gap is the average
 * across the gap itself, which is the very number it is compared against.
 */
function speedAt(points: TrackPoints, index: number, step: -1 | 1): number | undefined {
  const supplied = finiteAt(points.gsKt, index);
  if (supplied !== undefined) {
    return supplied;
  }
  const other = index + step;
  const t = finiteAt(points.t, index);
  const otherT = finiteAt(points.t, other);
  const lat = finiteAt(points.lat, index);
  const lon = finiteAt(points.lon, index);
  const otherLat = finiteAt(points.lat, other);
  const otherLon = finiteAt(points.lon, other);
  if (
    t === undefined ||
    otherT === undefined ||
    t === otherT ||
    lat === undefined ||
    lon === undefined ||
    otherLat === undefined ||
    otherLon === undefined
  ) {
    return undefined;
  }
  return haversineNm(lat, lon, otherLat, otherLon) / (Math.abs(t - otherT) / 3_600_000);
}

/**
 * Whether the silence between fix `index - 1` and fix `index` hides a ground
 * contact on a runway — see "The runway dropout" in the module comment for
 * the track this was written from and why each condition is needed.
 */
function runwayGapAt(
  points: TrackPoints,
  referenceAt: (index: number) => number | undefined,
  index: number,
  minGapMs: number,
): boolean {
  const before = index - 1;
  const t0 = finiteAt(points.t, before);
  const t1 = finiteAt(points.t, index);
  if (t0 === undefined || t1 === undefined) {
    return false;
  }
  const gapMs = t1 - t0;
  if (gapMs < minGapMs || gapMs > TOUCH_AND_GO_MAX_GROUND_MS) {
    return false;
  }

  const lat0 = finiteAt(points.lat, before);
  const lon0 = finiteAt(points.lon, before);
  const lat1 = finiteAt(points.lat, index);
  const lon1 = finiteAt(points.lon, index);
  if (lat0 === undefined || lon0 === undefined || lat1 === undefined || lon1 === undefined) {
    return false;
  }
  const field0 = nearestAerodrome(lat0, lon0);
  const field1 = nearestAerodrome(lat1, lon1);
  if (
    field0 === undefined ||
    field0.aerodrome !== field1?.aerodrome ||
    field0.distanceNm > RUNWAY_GAP_AERODROME_RADIUS_NM ||
    field1.distanceNm > RUNWAY_GAP_AERODROME_RADIUS_NM
  ) {
    return false;
  }

  for (const fix of [before, index]) {
    const alt = finiteAt(points.altFt, fix);
    const reference = referenceAt(fix);
    if (alt === undefined || reference === undefined || alt - reference > NEAR_GROUND_MARGIN_FT) {
      return false;
    }
  }

  const line = bearingDeg(lat0, lon0, lat1, lon1);
  const inbound = trackAt(points, before, -1);
  const outbound = trackAt(points, index, 1);
  if (
    inbound === undefined ||
    outbound === undefined ||
    angleBetweenDeg(inbound, line) > RUNWAY_GAP_ALIGNMENT_DEG ||
    angleBetweenDeg(outbound, line) > RUNWAY_GAP_ALIGNMENT_DEG
  ) {
    return false;
  }

  const speed0 = speedAt(points, before, -1);
  const speed1 = speedAt(points, index, 1);
  if (speed0 === undefined || speed1 === undefined) {
    return false;
  }
  const acrossKt = haversineNm(lat0, lon0, lat1, lon1) / (gapMs / 3_600_000);
  return acrossKt <= RUNWAY_GAP_SLOWDOWN_RATIO * ((speed0 + speed1) / 2);
}

/**
 * Slow and low is on the ground; fast, or well clear of the ground reference,
 * is flying. Anything else — slow at altitude, low with no speed to go on —
 * is `'unknown'` and neither starts nor ends a run.
 *
 * "Low" has two meanings here, and the module comment says why: on the
 * surface (`SURFACE_MARGIN_FT`) anything short of flying speed counts, while
 * up to `NEAR_GROUND_MARGIN_FT` it takes taxi speed.
 */
function classify(
  speedKt: number | undefined,
  altFt: number | undefined,
  referenceFt: number | undefined,
): Phase {
  const heightFt =
    altFt === undefined || referenceFt === undefined ? undefined : altFt - referenceFt;
  if (speedKt === undefined) {
    if (heightFt === undefined) {
      return 'unknown';
    }
    return heightFt <= NEAR_GROUND_MARGIN_FT ? 'ground' : 'air';
  }
  if (heightFt === undefined) {
    // Position and speed but no usable altitude: only the extremes are safe.
    if (speedKt >= FLYING_SPEED_KT) {
      return 'air';
    }
    return 'unknown';
  }
  if (heightFt > NEAR_GROUND_MARGIN_FT) {
    return 'air';
  }
  if (speedKt <= TAXI_SPEED_KT) {
    return 'ground';
  }
  if (heightFt <= SURFACE_MARGIN_FT && speedKt < FLYING_SPEED_KT) {
    // The rollout after touchdown, and the first half of a takeoff roll: on
    // the runway at 50–70kt, which is where most of a touch-and-go lives.
    return 'ground';
  }
  if (speedKt >= FLYING_SPEED_KT) {
    return 'air';
  }
  // Between taxi and flying speed, low but not on the surface: the last of
  // the approach and the first of the climb, and not worth guessing about.
  return 'unknown';
}

/** Whether any fix in `[start, end]` sits at a listed aerodrome. */
function runsAtAerodrome(points: TrackPoints, start: number, end: number): boolean {
  for (let i = start; i <= end; i += 1) {
    const lat = finiteAt(points.lat, i);
    const lon = finiteAt(points.lon, i);
    if (lat !== undefined && lon !== undefined && nearestAerodrome(lat, lon) !== undefined) {
      return true;
    }
  }
  return false;
}

function buildRuns(
  points: TrackPoints,
  speedsKt: (number | undefined)[],
  referenceAt: (index: number) => number | undefined,
): Run[] {
  const { altFt, t } = points;
  const breakMs = breakThresholdMs(t);
  const runwayGapMinMs = Math.max(
    RUNWAY_GAP_MIN_MS,
    (medianIntervalMs(t) ?? 0) * RUNWAY_GAP_SAMPLE_FACTOR,
  );
  const runs: Run[] = [];
  let current: Run | undefined;
  let lastFixMs: number | undefined;
  /*
   * Whether the recording has a hole between the last fix this loop could
   * classify and the one it is looking at now. It is cleared by every
   * classified fix, not only by the start of a run: a dropout in the middle
   * of the cruise is not evidence about the landing twenty minutes later.
   */
  let gapPending = false;

  const noteFix = (run: Run, index: number): void => {
    const alt = finiteAt(altFt, index);
    const reference = referenceAt(index);
    if (alt !== undefined && reference !== undefined && alt - reference <= SURFACE_MARGIN_FT) {
      run.onSurface = true;
    }
    const speed = speedsKt[index];
    if (speed !== undefined && speed <= TAXI_SPEED_KT) {
      run.slowedToTaxi = true;
    }
  };

  for (let i = 0; i < altFt.length; i += 1) {
    /*
     * A runway gap stands in for the ground run the recording never saw. It
     * spans the two fixes either side of the silence, so its duration is the
     * silence itself, and the run that follows starts on the second of them.
     * Already on the ground, there is nothing to add: the gap is part of it.
     */
    if (
      i > 0 &&
      current?.phase !== 'ground' &&
      runwayGapAt(points, referenceAt, i, runwayGapMinMs)
    ) {
      if (current !== undefined) {
        runs.push(current);
      }
      current = {
        phase: 'ground',
        startIndex: i - 1,
        endIndex: i,
        durationMs: 0,
        brokenFromPrevious: gapPending,
        atAerodrome: false,
        onSurface: true,
        slowedToTaxi: false,
        atTrackEdge: false,
        hidden: true,
        fixCount: 1,
      };
      gapPending = false;
    }
    const phase = classify(speedsKt[i], finiteAt(altFt, i), referenceAt(i));
    const ti = finiteAt(t, i);
    if (lastFixMs !== undefined && ti !== undefined && ti - lastFixMs > breakMs) {
      gapPending = true;
    }
    if (ti !== undefined) {
      lastFixMs = ti;
    }
    if (phase === 'unknown') {
      // Carries the current run along — an unreadable fix is not a change of
      // state, and treating it as one is how noise becomes a landing.
      continue;
    }
    if (current?.phase !== phase) {
      if (current !== undefined) {
        runs.push(current);
      }
      current = {
        phase,
        startIndex: i,
        endIndex: i,
        durationMs: 0,
        brokenFromPrevious: gapPending,
        atAerodrome: false,
        onSurface: false,
        slowedToTaxi: false,
        atTrackEdge: false,
        hidden: false,
        fixCount: 1,
      };
      noteFix(current, i);
      gapPending = false;
      continue;
    }
    current.endIndex = i;
    current.fixCount += 1;
    noteFix(current, i);
    gapPending = false;
  }
  if (current !== undefined) {
    runs.push(current);
  }

  for (const run of runs) {
    const start = finiteAt(t, run.startIndex);
    const end = finiteAt(t, run.endIndex);
    run.durationMs = start === undefined || end === undefined ? 0 : end - start;
    if (run.phase === 'ground') {
      run.atAerodrome = runsAtAerodrome(points, run.startIndex, run.endIndex);
    }
  }
  /*
   * The first and last runs hold the fixes where the recording began and
   * ended. Nothing more is coming on either side, so a brief ground contact
   * there is the end of the evidence rather than a fragment of it — see the
   * module comment.
   */
  const first = runs[0];
  const last = runs[runs.length - 1];
  if (first !== undefined) {
    first.atTrackEdge = true;
  }
  if (last !== undefined) {
    last.atTrackEdge = true;
  }
  return runs;
}

/**
 * Drops runs too short to be real and merges what is left, repeatedly, until
 * nothing more can be dropped.
 *
 * The repetition matters: removing a two-second "landing" in the middle of
 * the cruise leaves two airborne runs that are really one, and the merged
 * run is what the next pass has to judge.
 */
function significantRuns(runs: Run[], timed: boolean): Run[] {
  const isReal = (run: Run): boolean => {
    if (run.phase === 'air') {
      return timed ? run.durationMs >= MIN_AIRBORNE_MS : run.fixCount >= 2;
    }
    // A track that opens on the takeoff roll or stops on short final gives
    // only a handful of surface fixes, and no amount of waiting will produce
    // more: take them at face value.
    if (run.atTrackEdge && run.onSurface) {
      return true;
    }
    // A wheels-on-the-runway touch-and-go is over in seconds, so time alone
    // cannot be the test — but a known aerodrome under it can.
    if (run.atAerodrome) {
      return true;
    }
    // Away from every listed aerodrome, a ground contact has to both last and
    // actually slow down. Without the second half, a long low run over flat
    // country at 70kt would read as a landing on a paddock.
    if (!run.slowedToTaxi) {
      return false;
    }
    return timed ? run.durationMs >= MIN_GROUND_MS : run.fixCount >= 2;
  };

  let working = runs;
  for (let pass = 0; pass < 10; pass += 1) {
    const kept = working.filter(isReal);
    const merged: Run[] = [];
    for (const run of kept) {
      const previous = merged[merged.length - 1];
      if (previous?.phase === run.phase) {
        previous.endIndex = run.endIndex;
        previous.durationMs += run.durationMs;
        previous.fixCount += run.fixCount;
        previous.atAerodrome = previous.atAerodrome || run.atAerodrome;
        previous.onSurface = previous.onSurface || run.onSurface;
        previous.slowedToTaxi = previous.slowedToTaxi || run.slowedToTaxi;
        previous.atTrackEdge = previous.atTrackEdge || run.atTrackEdge;
        previous.hidden = previous.hidden || run.hidden;
        previous.brokenFromPrevious = previous.brokenFromPrevious || run.brokenFromPrevious;
        continue;
      }
      merged.push(run);
    }
    if (merged.length === working.length) {
      return merged;
    }
    working = merged;
  }
  return working;
}

/**
 * Whether a track that begins or ends in the air did so immediately after a
 * departure, or immediately before an arrival, at the aerodrome beside it.
 *
 * FR24 coverage can also start or stop just short of the runway — the
 * recording ending on short final at 300ft and 52kt, 0.3nm from the
 * threshold, with the touchdown itself never sent — and the fixes then
 * contain nothing that says "landing". The earlier rules count nothing at
 * all in that case, and the pilot loses a real one from the draft.
 *
 * Three things together are enough to read it, and each is needed:
 *
 *  - a listed aerodrome within `EDGE_EVENT_AERODROME_RADIUS_NM` of the edge
 *    fix — deliberately tighter than `AERODROME_MATCH_RADIUS_NM`, since this
 *    rule has to explain a fix that is not a ground contact at all;
 *  - the edge fix within `EDGE_EVENT_HEIGHT_FT` of the local surface, because
 *    an aircraft transiting at its cruising level did not just leave;
 *  - the distance to that aerodrome opening (for a departure) or closing (for
 *    an arrival) by `EDGE_TREND_NM` over the next few minutes, which is what
 *    separates leaving a field from flying past it.
 *
 * This is the one inference in the module that counts an event the fixes do
 * not contain, so it is deliberately the narrowest: it is consulted only at
 * the two ends of the recording, and only when the earlier rules found no
 * ground contact there to reason from. A departure or arrival caught further
 * out than a mile or so — `41ae2801`'s opens 4.3nm off Opotiki, well beyond
 * this rule's reach — is not inferred at all; the pilot fills that one in.
 * A count that does come from here still gets marked
 * (`GroundEvents.edgeInferred`) so the caller can flag it for a pilot to
 * confirm rather than trust outright.
 */
function edgeEventAerodrome(
  points: TrackPoints,
  referenceAt: (index: number) => number | undefined,
  index: number,
  direction: 'departure' | 'arrival',
): boolean {
  const { altFt, t, lat, lon } = points;
  const edgeLat = finiteAt(lat, index);
  const edgeLon = finiteAt(lon, index);
  const edgeMs = finiteAt(t, index);
  if (edgeLat === undefined || edgeLon === undefined || edgeMs === undefined) {
    return false;
  }
  const match = nearestAerodrome(edgeLat, edgeLon);
  if (
    match?.aerodrome.lat === undefined ||
    match.aerodrome.lon === undefined ||
    match.distanceNm > EDGE_EVENT_AERODROME_RADIUS_NM
  ) {
    return false;
  }
  const alt = finiteAt(altFt, index);
  const reference = referenceAt(index);
  if (alt === undefined || reference === undefined || alt - reference > EDGE_EVENT_HEIGHT_FT) {
    return false;
  }
  // On the surface almost over the runway, the fix says it on its own: see
  // the module comment on a track that opens at 0ft and 87kt.
  if (alt - reference <= SURFACE_MARGIN_FT) {
    return true;
  }

  /*
   * A departure is read forwards from the track's first fix and an arrival
   * backwards from its last, so both ask the same question: across the few
   * minutes on the *inside* of the recording, did the aerodrome fall behind?
   */
  const step = direction === 'departure' ? 1 : -1;
  let furthest: number | undefined;
  for (let i = index + step; i >= 0 && i < altFt.length; i += step) {
    const ti = finiteAt(t, i);
    if (ti !== undefined && Math.abs(ti - edgeMs) > EDGE_TREND_MS) {
      break;
    }
    const lati = finiteAt(lat, i);
    const loni = finiteAt(lon, i);
    if (lati === undefined || loni === undefined) {
      continue;
    }
    const distanceNm = haversineNm(lati, loni, match.aerodrome.lat, match.aerodrome.lon);
    furthest = furthest === undefined ? distanceNm : Math.max(furthest, distanceNm);
  }
  return furthest !== undefined && furthest - match.distanceNm >= EDGE_TREND_NM;
}

/**
 * Counts the crossings between the ground and the air, once the track has
 * been reduced to runs worth believing.
 *
 * A crossing that straddles a break in the recording is only counted when
 * the ground side of it is at a listed aerodrome: with the aircraft slow and
 * low on a runway, a landing did happen even though the recording missed it,
 * while a hole in coverage between two airborne fixes is just a hole.
 */
export function detectGroundEvents(points: TrackPoints): GroundEvents | undefined {
  const { altFt, t, lat, lon } = points;
  if (altFt.length === 0) {
    return undefined;
  }
  const speedsKt = resolveSpeedsKt(points);
  const timed = Array.from(t).filter((value) => Number.isFinite(value)).length >= 2;
  const referenceAt = groundReferenceFt(points, speedsKt);
  const runs = significantRuns(buildRuns(points, speedsKt, referenceAt), timed);
  if (runs.length === 0) {
    return undefined;
  }

  const isNightAt = (index: number): boolean => {
    const ti = finiteAt(t, index);
    const lati = finiteAt(lat, index);
    const loni = finiteAt(lon, index);
    return (
      ti !== undefined &&
      lati !== undefined &&
      loni !== undefined &&
      isNight(new Date(ti), lati, loni)
    );
  };

  let takeoffCount = 0;
  let nightTakeoffCount = 0;
  let landingCount = 0;
  let nightLandingCount = 0;
  let touchAndGoCount = 0;
  let breaksSkipped = false;
  let edgeInferred = false;
  let gapInferred = false;
  const events: GroundEvent[] = [];

  for (let i = 1; i < runs.length; i += 1) {
    const previous = runs[i - 1];
    const run = runs[i];
    if (previous === undefined || run === undefined || previous.phase === run.phase) {
      continue;
    }
    const groundSide = run.phase === 'ground' ? run : previous;
    if (run.brokenFromPrevious && !groundSide.atAerodrome) {
      breaksSkipped = true;
      continue;
    }
    if (groundSide.hidden) {
      gapInferred = true;
    }
    events.push({
      kind: run.phase === 'air' ? 'takeoff' : 'landing',
      index: run.startIndex,
      inferred: groundSide.hidden,
    });
    if (run.phase === 'air') {
      takeoffCount += 1;
      if (isNightAt(run.startIndex)) {
        nightTakeoffCount += 1;
      }
      continue;
    }
    landingCount += 1;
    if (isNightAt(run.startIndex)) {
      nightLandingCount += 1;
    }
    // A touch-and-go is a landing the aircraft flew straight back out of —
    // read from the ground run's own length and what follows it, not from
    // "everything but the last one", which mislabelled a two-leg flight's
    // first full-stop landing as a touch-and-go.
    const next = runs[i + 1];
    if (
      next?.phase === 'air' &&
      !next.brokenFromPrevious &&
      (!timed || run.durationMs <= TOUCH_AND_GO_MAX_GROUND_MS)
    ) {
      touchAndGoCount += 1;
    }
  }

  /*
   * Finally the two ends of the recording, where the fixes stop but the
   * flight did not. Consulted only where the runs found no ground contact to
   * count from — a track that begins or ends on the ground has already been
   * read, and re-reading it here would double the event.
   */
  const first = runs[0];
  if (
    first?.phase === 'air' &&
    edgeEventAerodrome(points, referenceAt, first.startIndex, 'departure')
  ) {
    takeoffCount += 1;
    edgeInferred = true;
    events.unshift({ kind: 'takeoff', index: first.startIndex, inferred: true });
    if (isNightAt(first.startIndex)) {
      nightTakeoffCount += 1;
    }
  }
  const last = runs[runs.length - 1];
  if (last?.phase === 'air' && edgeEventAerodrome(points, referenceAt, last.endIndex, 'arrival')) {
    landingCount += 1;
    edgeInferred = true;
    events.push({ kind: 'landing', index: last.endIndex, inferred: true });
    if (isNightAt(last.endIndex)) {
      nightLandingCount += 1;
    }
  }

  return {
    events,
    takeoffCount,
    nightTakeoffCount,
    landingCount,
    nightLandingCount,
    touchAndGoCount,
    breaksSkipped,
    edgeInferred,
    gapInferred,
  };
}
